import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { GraduationCap, Search, Quote, Star, X, MapPin, Trophy, ZoomIn, Award, ChevronLeft, ChevronRight, Camera, Download, Share2, Play, Pause } from 'lucide-react';
import { alumni, contactEmails, contactMailto, heroImages, iceLogo, memoryArchive } from '../mock';
import { getChampionship, getAlumniGallery } from '../lib/api';

export default function Alumni() {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null); // memory object or null
  const [champion, setChampion] = useState(null);
  const [gallery, setGallery] = useState(null);   // { photos, currentIndex, nextRotation }
  const [galleryIdx, setGalleryIdx] = useState(0); // manual browse index
  const [lightboxNotice, setLightboxNotice] = useState('');
  const [isSlideshowPlaying, setIsSlideshowPlaying] = useState(false);
  const lightboxTouch = useRef(null);

  useEffect(() => {
    getChampionship('2013-peewee-sb').then(setChampion).catch(() => {});
  }, []);

  useEffect(() => {
    // load gallery when modal opens on someone with a slug
    setGallery(null);
    setGalleryIdx(0);
    if (selected && selected.slug) {
      getAlumniGallery(selected.slug).then((g) => {
        setGallery(g);
        setGalleryIdx(g.currentIndex || 0);
      }).catch(() => setGallery(null));
    }
  }, [selected]);

  const featured = useMemo(() => alumni.find((a) => a.featured), []);
  const others = useMemo(() => alumni.filter((a) => !a.featured), []);

  const filtered = useMemo(() => {
    if (!query.trim()) return others;
    const q = query.toLowerCase();
    return others.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.year.toLowerCase().includes(q) ||
        a.position.toLowerCase().includes(q) ||
        (a.school || '').toLowerCase().includes(q)
    );
  }, [query, others]);

  const modalGallery = useMemo(() => {
    if (gallery?.photos?.length) return gallery;
    if (!selected?.additionalPhotos?.length) return null;
    return {
      photos: [
        { id: `${selected.slug}-profile`, url: selected.img, caption: selected.profileCaption || `${selected.name} profile photo`, fit: selected.profileCaption ? 'contain' : undefined },
        ...selected.additionalPhotos,
      ],
      currentIndex: 0,
      nextRotation: null,
    };
  }, [gallery, selected]);

  const openLightbox = (memory) => {
    setLightboxNotice('');
    setIsSlideshowPlaying(false);
    setLightbox(memory);
  };

  const closeLightbox = useCallback(() => {
    setIsSlideshowPlaying(false);
    setLightboxNotice('');
    setLightbox(null);
  }, []);

  const navigateLightbox = useCallback((direction) => {
    setLightbox((current) => {
      if (!current) return current;
      const currentIndex = memoryArchive.findIndex((memory) => memory.id === current.id);
      const nextIndex = (currentIndex + direction + memoryArchive.length) % memoryArchive.length;
      return memoryArchive[nextIndex];
    });
    setLightboxNotice('');
  }, []);

  useEffect(() => {
    if (!lightbox) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') navigateLightbox(-1);
      if (event.key === 'ArrowRight') navigateLightbox(1);
      if (event.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeLightbox, lightbox, navigateLightbox]);

  useEffect(() => {
    if (!lightbox || !isSlideshowPlaying) return undefined;
    const timer = window.setTimeout(() => navigateLightbox(1), 5000);
    return () => window.clearTimeout(timer);
  }, [isSlideshowPlaying, lightbox, navigateLightbox]);

  const handleLightboxTouchStart = (event) => {
    const touch = event.touches[0];
    lightboxTouch.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleLightboxTouchEnd = (event) => {
    if (!lightboxTouch.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - lightboxTouch.current.x;
    const deltaY = touch.clientY - lightboxTouch.current.y;
    lightboxTouch.current = null;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    navigateLightbox(deltaX < 0 ? 1 : -1);
  };

  const shareLightbox = async (event) => {
    event.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Forever Patriots', text: lightbox.title, url: lightbox.img });
        setLightboxNotice('Share sheet opened');
      } else {
        await navigator.clipboard.writeText(lightbox.img);
        setLightboxNotice('Photo link copied');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') setLightboxNotice('Sharing is unavailable on this device');
    }
  };

  const downloadLightbox = async (event) => {
    event.stopPropagation();
    setLightboxNotice('Preparing download…');
    try {
      const response = await fetch(lightbox.img);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `forever-patriots-${lightbox.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setLightboxNotice('Download started');
    } catch (_error) {
      window.open(lightbox.img, '_blank', 'noopener,noreferrer');
      setLightboxNotice('Photo opened for saving');
    }
  };

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[1]} alt="stadium" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <img src={iceLogo} alt="ICE Logo" className="h-14 w-auto object-contain" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs">Patriots For Life</div>
          </div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none">ALUMNI</h1>
          <p className="text-white/70 max-w-2xl mt-6 text-lg">
            Once a Patriot, always a Patriot. Meet the scholars, athletes, and leaders who came up through the Brookline JP program.
          </p>
        </div>
      </section>

      <div className="stripe-sep" />

      {/* Featured Alumnus */}
      {featured && (
        <section className="py-24 bg-white" data-testid="alumni-featured-profile">
          <div className="max-w-7xl mx-auto px-6">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-8 text-center">Featured Alumnus</div>
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full border-4 border-[#c8102e]" />
                <div className="relative aspect-[3/4] max-h-[640px] overflow-hidden bg-[#0a1f44]">
                  <img src={featured.img} alt={featured.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-[#c8102e] text-white px-6 py-4">
                  <div className="font-display text-4xl leading-none">25</div>
                  <div className="font-heading uppercase tracking-widest text-[10px] mt-1">Jersey Number</div>
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 bg-[#0a1f44] text-white px-3 py-1.5 mb-5">
                  <Star className="w-3.5 h-3.5 text-[#c8102e]" />
                  <span className="font-heading uppercase tracking-widest text-[10px]">Hall of Patriots</span>
                </div>
                <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl leading-tight mb-3" data-testid="alumni-featured-name">
                  {featured.name.split(' ')[0].toUpperCase()}<br />
                  <span className="text-[#c8102e]">{featured.name.split(' ').slice(1).join(' ').toUpperCase()}</span>
                </h2>
                <div className="flex flex-wrap gap-2 mb-6">
                  <span className="font-heading uppercase tracking-widest text-[11px] bg-[#0a1f44]/5 text-[#0a1f44] px-3 py-1.5">{featured.year}</span>
                  <span className="font-heading uppercase tracking-widest text-[11px] bg-[#0a1f44]/5 text-[#0a1f44] px-3 py-1.5">{featured.position}</span>
                  <span className="font-heading uppercase tracking-widest text-[11px] bg-[#0a1f44]/5 text-[#0a1f44] px-3 py-1.5 inline-flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-[#c8102e]" /> {featured.school}
                  </span>
                  {featured.college && (
                    <span data-testid="alumni-featured-college" className="font-heading uppercase tracking-widest text-[11px] bg-[#c8102e] text-white px-3 py-1.5 inline-flex items-center gap-1.5">
                      <GraduationCap className="w-3 h-3" /> {featured.college}
                    </span>
                  )}
                </div>
                <div className="relative bg-[#f5f6f8] border-l-4 border-[#c8102e] p-6 mb-6">
                  <Quote className="w-8 h-8 text-[#c8102e]/25 absolute top-4 right-4" />
                  <p className="text-[#0a1f44]/85 italic leading-relaxed text-lg">&ldquo;{featured.quote}&rdquo;</p>
                </div>
                <p className="text-[#0a1f44]/75 leading-relaxed" data-testid="alumni-featured-bio">{featured.bio}</p>
                <div className="mt-6 flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-[#c8102e]" />
                  <span className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/70">Championship Alumnus &bull; Program Ambassador</span>
                </div>
              </div>
            </div>
            {featured.highlight && (
              <figure className="mt-20 pt-12 border-t-2 border-[#0a1f44]/10 max-w-5xl mx-auto grid lg:grid-cols-12 gap-8 items-center" data-testid="alumni-featured-highlight">
                <div className="lg:col-span-7 relative overflow-hidden border border-[#0a1f44]/10 shadow-lg">
                  <img src={featured.highlight.img} alt={featured.highlight.alt} data-testid="alumni-featured-highlight-image" className="w-full h-auto object-contain" loading="lazy" decoding="async" />
                  <span className="absolute left-0 top-4 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5">Record Book</span>
                </div>
                <figcaption className="lg:col-span-5">
                  <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-3">{featured.highlight.date}</div>
                  <h3 className="font-display text-[#0a1f44] text-3xl md:text-4xl leading-tight">100 YARDS. <span className="text-[#c8102e]">HISTORY.</span></h3>
                  <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60 mt-2">{featured.highlight.label}</div>
                  <blockquote className="mt-5 border-l-4 border-[#0a1f44] pl-4 text-[#0a1f44]/80 leading-relaxed" data-testid="alumni-featured-highlight-text">“{featured.highlight.text}”</blockquote>
                  <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/50 mt-3">— {featured.highlight.source}</div>
                </figcaption>
              </figure>
            )}
            {featured.showcaseImg && (
              <figure className="mt-20 pt-12 border-t-2 border-[#0a1f44]/10 max-w-5xl mx-auto" data-testid="alumni-featured-showcase">
                <div className="mb-6 text-center">
                  <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-3">Springfield College Football</div>
                  <h3 className="font-display text-[#0a1f44] text-4xl md:text-5xl">SENIOR SHOWCASE</h3>
                </div>
                <img
                  src={featured.showcaseImg}
                  alt={featured.showcaseAlt}
                  data-testid="alumni-featured-showcase-image"
                  className="w-full h-auto object-contain border border-[#0a1f44]/10 shadow-lg"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/55 text-center mt-4" data-testid="alumni-featured-showcase-caption">
                  Kejuan Farquharson · Tradition Never Graduates
                </figcaption>
              </figure>
            )}
          </div>
        </section>
      )}

      {/* Alumni Directory */}
      <section className="py-24 bg-[#f5f6f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Alumni Directory</div>
              <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">THE PATRIOTS FAMILY</h2>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-[#0a1f44]/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, year, position..."
                data-testid="alumni-search-input"
                className="bg-white border-2 border-[#0a1f44]/10 focus:border-[#c8102e] outline-none pl-10 pr-4 py-2.5 w-full md:w-80 text-sm"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border-2 border-dashed border-[#0a1f44]/15 p-16 text-center">
              <GraduationCap className="w-10 h-10 text-[#c8102e] mx-auto mb-3" />
              <p className="text-[#0a1f44]/70">No alumni match that search.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filtered.map((a) => (
                <button
                  type="button"
                  key={a.name}
                  onClick={() => setSelected(a)}
                  data-testid={`alumni-card-${a.slug || a.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="group relative overflow-hidden bg-[#0a1f44] cursor-pointer hover-lift text-left"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={a.img}
                      alt={a.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      style={a.imgTransform ? { transform: a.imgTransform, transformOrigin: '50% 50%' } : undefined}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44] via-[#0a1f44]/40 to-transparent" />
                  </div>
                  <div className="absolute top-3 right-3 bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-2 py-1">
                    {a.year.replace('Class of ', "'")}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                    <div className="w-8 h-0.5 bg-[#c8102e] mb-2" />
                    <div className="font-display text-lg tracking-wide leading-tight">{a.name.toUpperCase()}</div>
                    <div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e] mt-1">{a.position}</div>
                    <div className="text-white/60 text-[11px] mt-1">{a.school}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 2013 Pee Wee Super Bowl Championship Team */}
      {champion && (
        <section id="championships" className="py-24 bg-[#0a1f44] relative overflow-hidden" data-testid="championship-section">
          <div className="absolute inset-0 opacity-20">
            <img src={champion.img} alt="" className="w-full h-full object-cover" style={{ objectPosition: 'center 40%' }} />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44] via-[#0a1f44]/70 to-[#0a1f44]" />
          </div>
          <div className="relative max-w-6xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">
                <Award className="w-4 h-4" /> Championship Team · Est. {champion.year}
              </div>
              <h2 className="font-display text-white text-5xl md:text-6xl leading-tight">{champion.title}</h2>
              <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-2 text-white/70 text-sm font-heading uppercase tracking-widest">
                {champion.event && <span data-testid="champion-event">{champion.event}</span>}
                {champion.dates && <span className="text-[#c8102e]">·  {champion.dates}</span>}
                {champion.location && <span>·  {champion.location}</span>}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-8">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 overflow-hidden" data-testid="champion-photo">
                  <img src={champion.img} alt={champion.title} className="w-full h-auto object-cover" style={{ objectPosition: 'center 40%' }} />
                  <div className="p-4 text-white text-xs font-heading uppercase tracking-widest text-center border-t border-white/10">
                    {champion.location}
                  </div>
                </div>

                {/* Coaching Staff (public) */}
                {(champion.coaches || []).length > 0 && (
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-6" data-testid="champion-coaches">
                    <div className="flex items-center gap-2 mb-4">
                      <Award className="w-5 h-5 text-[#c8102e]" />
                      <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-[#c8102e]">Coaching Staff</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {champion.coaches.map((c) => (
                        <div key={c.id} data-testid={`champion-coach-${c.id}`} className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#0a1f44] flex-shrink-0 overflow-hidden flex items-center justify-center border border-white/10">
                            {c.img ? (
                              <img src={c.img} alt={c.name} className="w-full h-full object-cover" />
                            ) : (
                              <Award className="w-5 h-5 text-white/40" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-white font-display text-base truncate">{c.name}</div>
                            <div className="font-heading uppercase tracking-widest text-[9px] text-[#c8102e]">{c.role || 'Coach'}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white shadow-2xl" data-testid="champion-roster">
                <div className="bg-[#c8102e] text-white px-6 py-4 flex items-center justify-between">
                  <div>
                    <div className="font-heading uppercase tracking-[0.3em] text-[10px] text-white/80">Championship Roster</div>
                    <div className="font-display text-2xl">{champion.players.length} PATRIOT{champion.players.length !== 1 ? 'S' : ''}</div>
                  </div>
                  <Trophy className="w-8 h-8" />
                </div>
                {champion.players.length === 0 ? (
                  <div className="p-12 text-center text-[#0a1f44]/60" data-testid="champion-empty">
                    <Trophy className="w-8 h-8 text-[#c8102e] mx-auto mb-3" />
                    <p className="font-heading uppercase tracking-widest text-xs">Roster coming soon</p>
                    <p className="text-sm mt-2">Player names will appear here as the coaching staff uploads them.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-[#0a1f44]/10 max-h-[520px] overflow-y-auto">
                    {champion.players.map((p, idx) => (
                      <li key={p.id} data-testid={`champion-player-${p.id}`} className="flex items-center gap-4 px-6 py-3 hover:bg-[#c8102e]/5 transition-colors">
                        <span className="font-mono text-[#0a1f44]/40 text-xs w-6">{String(idx + 1).padStart(2, '0')}</span>
                        <span className="flex-1 text-[#0a1f44] font-medium">{p.name}</span>
                        {p.jersey && (
                          <span className="font-mono text-[#c8102e] font-bold">#{p.jersey}</span>
                        )}
                        {p.position && (
                          <span className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60 w-14 text-right">{p.position}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Alumni Memories — Team Photo Archive */}
      <section className="py-24 bg-white" data-testid="alumni-memories">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Team Photo Archive</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">FOREVER PATRIOTS</h2>
            <p className="text-[#0a1f44]/70 mt-4">Every championship, sideline moment, and homecoming captured. Tap any photo to enlarge.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8" data-testid="memory-archive">
            {memoryArchive.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => openLightbox(m)}
                data-testid={`memory-card-${m.id}`}
                aria-label={`Enlarge ${m.title}`}
                className="hover-lift group min-w-0 bg-white border border-[#0a1f44]/10 shadow-sm text-left focus:outline-none focus:ring-2 focus:ring-[#c8102e]"
              >
                <div className="aspect-[4/3] bg-[#0a1f44] overflow-hidden relative">
                  <img
                    src={m.img}
                    alt={`${m.title} · ${m.tag}`}
                    loading="lazy"
                    decoding="async"
                    className={`w-full h-full ${m.imgRotate || m.imgFit === 'contain' ? 'object-contain' : 'object-cover group-hover:scale-[1.025]'} transition-transform duration-500`}
                    style={{
                      ...(m.imgPosition && !m.imgRotate ? { objectPosition: m.imgPosition } : {}),
                      ...(m.imgRotate ? { transform: `rotate(${m.imgRotate}deg) scale(0.76)` } : {}),
                      imageRendering: 'auto',
                      filter: 'contrast(1.06) saturate(1.08) brightness(1.01)',
                      backfaceVisibility: 'hidden'
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute bottom-3 right-3 bg-white/95 text-[#0a1f44] p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="w-4 h-4" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {lightbox && (
          <div
            className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
            onClick={closeLightbox}
            data-testid="memory-lightbox"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              data-testid="memory-lightbox-close"
              className="absolute top-4 right-4 md:top-6 md:right-6 bg-white/10 hover:bg-white/20 text-white p-2 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); navigateLightbox(-1); }}
              data-testid="memory-lightbox-previous"
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 bg-black/45 hover:bg-[#c8102e] text-white p-2.5 md:p-3 transition-colors"
              aria-label="Previous photo"
              title="Previous photo"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={(event) => { event.stopPropagation(); navigateLightbox(1); }}
              data-testid="memory-lightbox-next"
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 bg-black/45 hover:bg-[#c8102e] text-white p-2.5 md:p-3 transition-colors"
              aria-label="Next photo"
              title="Next photo"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-10 flex items-center gap-2" data-testid="memory-lightbox-actions">
              {lightboxNotice && <span className="bg-black/70 text-white text-xs px-3 py-2" data-testid="memory-lightbox-notice" aria-live="polite">{lightboxNotice}</span>}
              <button
                type="button"
                onClick={(event) => { event.stopPropagation(); setIsSlideshowPlaying((playing) => !playing); setLightboxNotice(''); }}
                data-testid="memory-lightbox-slideshow-toggle"
                className={`text-white p-2.5 transition-colors ${isSlideshowPlaying ? 'bg-[#c8102e] hover:bg-[#e11d34]' : 'bg-white/10 hover:bg-white/20'}`}
                aria-label={isSlideshowPlaying ? 'Pause slideshow' : 'Play slideshow'}
                title={isSlideshowPlaying ? 'Pause slideshow' : 'Play slideshow'}
              >
                {isSlideshowPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </button>
              <button
                type="button"
                onClick={shareLightbox}
                data-testid="memory-lightbox-share"
                className="bg-white/10 hover:bg-white/20 text-white p-2.5 transition-colors"
                aria-label="Share photo"
                title="Share photo"
              >
                <Share2 className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={downloadLightbox}
                data-testid="memory-lightbox-download"
                className="bg-white/10 hover:bg-white/20 text-white p-2.5 transition-colors"
                aria-label="Download photo"
                title="Download photo"
              >
                <Download className="w-5 h-5" />
              </button>
            </div>
            <span className="sr-only" data-testid="memory-lightbox-position" aria-live="polite">
              Photo {memoryArchive.findIndex((memory) => memory.id === lightbox.id) + 1} of {memoryArchive.length}
            </span>
            <div
              className="max-w-6xl w-full touch-pan-y"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleLightboxTouchStart}
              onTouchEnd={handleLightboxTouchEnd}
            >
              <div className="bg-[#0a1f44] flex items-center justify-center max-h-[80vh] overflow-hidden">
                <img
                  key={lightbox.id}
                  src={lightbox.img}
                  alt={lightbox.title}
                  className="max-w-full max-h-[80vh] object-contain animate-in fade-in duration-300"
                  style={lightbox.imgRotate ? { transform: `rotate(${lightbox.imgRotate}deg)` } : undefined}
                />
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Submit CTA */}
      <section className="py-20 bg-[#0a1f44] relative overflow-hidden">
        <div className="absolute inset-0 stars-bg" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <img src={iceLogo} alt="ICE Logo" className="h-16 w-auto object-contain mx-auto mb-6" />
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Are You An Alumnus?</div>
          <h2 className="font-display text-white text-5xl md:text-6xl mb-5">JOIN THE ROLL CALL</h2>
          <p className="text-white/75 text-lg mb-8 max-w-2xl mx-auto">
            Once a Patriot, always a Patriot. Send us your name, class year, and photo and we&apos;ll add you to the alumni wall.
          </p>
          <a href={`${contactMailto}?subject=Alumni%20Submission`} data-testid="alumni-submit-story" className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-8 py-4 transition-colors">
            Submit Your Story
          </a>
          <div className="mt-4 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-white/65" data-testid="alumni-contact-emails">
            {contactEmails.map((email, index) => (
              <a key={email} href={`mailto:${email}`} data-testid={`alumni-contact-email-${index + 1}`} className="hover:text-white break-all transition-colors">{email}</a>
            ))}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 fade-in" onClick={() => setSelected(null)} data-testid="alumni-detail-modal">
          <div className="bg-white max-w-2xl w-full relative grid md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} data-testid="alumni-detail-modal-close" aria-label="Close alumni details" className="absolute top-3 right-3 z-10 text-white bg-[#0a1f44]/70 hover:bg-[#c8102e] w-9 h-9 flex items-center justify-center">
              <X className="w-5 h-5" />
            </button>
            <div className="aspect-[3/4] md:aspect-auto bg-[#0a1f44] overflow-hidden relative" data-testid="alumni-modal-photo">
              {modalGallery?.photos?.length > 0 ? (
                <>
                  <img
                    src={modalGallery.photos[galleryIdx].url}
                    alt={modalGallery.photos[galleryIdx].caption || selected.name}
                    className={`w-full h-full transition-opacity duration-500 ${modalGallery.photos[galleryIdx].fit === 'contain' ? 'object-contain' : 'object-cover'}`}
                    data-testid="alumni-gallery-photo"
                  />
                  {modalGallery.photos.length > 1 && (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); setGalleryIdx((galleryIdx - 1 + modalGallery.photos.length) % modalGallery.photos.length); }}
                        data-testid="alumni-gallery-prev"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-[#c8102e] text-white w-9 h-9 flex items-center justify-center transition-colors"
                        aria-label="Previous photo"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setGalleryIdx((galleryIdx + 1) % modalGallery.photos.length); }}
                        data-testid="alumni-gallery-next"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-[#c8102e] text-white w-9 h-9 flex items-center justify-center transition-colors"
                        aria-label="Next photo"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-16 left-0 right-0 flex justify-center gap-1.5" data-testid="alumni-gallery-dots">
                        {modalGallery.photos.map((p, i) => (
                          <button
                            key={p.id}
                            onClick={(e) => { e.stopPropagation(); setGalleryIdx(i); }}
                            data-testid={`alumni-gallery-dot-${i}`}
                            aria-label={`Photo ${i + 1}`}
                            className={`h-1.5 transition-all ${i === galleryIdx ? 'w-6 bg-[#c8102e]' : 'w-1.5 bg-white/60'}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {selected.galleryLabel && (
                    <span className="absolute left-0 top-4 bg-[#c8102e] text-white font-heading uppercase tracking-[0.3em] text-[10px] px-3 py-1.5" data-testid="alumni-gallery-label">{selected.galleryLabel} · {galleryIdx + 1}/{modalGallery.photos.length}</span>
                  )}
                  {modalGallery.photos[galleryIdx].caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
                      <div className="flex items-center gap-2 text-white text-xs">
                        <Camera className="w-3 h-3 text-[#c8102e]" />
                        <span className="font-heading uppercase tracking-widest text-[10px]">{modalGallery.photos[galleryIdx].caption}</span>
                      </div>
                      {modalGallery.nextRotation && galleryIdx === (modalGallery.currentIndex ?? 0) && (
                        <div className="text-[10px] text-[#c8102e] font-heading uppercase tracking-widest mt-1" data-testid="alumni-gallery-rotation-label">
                          Photo of the month · rotates {new Date(modalGallery.nextRotation).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <img
                  src={selected.img}
                  alt={selected.name}
                  className="w-full h-full object-cover"
                  style={selected.imgTransform ? { transform: selected.imgTransform, transformOrigin: '50% 50%' } : undefined}
                />
              )}
            </div>
            <div className="p-6 md:p-8 flex flex-col">
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-2">{selected.year}</div>
              <h3 className="font-display text-3xl text-[#0a1f44] leading-tight mb-1">{selected.name.toUpperCase()}</h3>
              <div className="text-[#0a1f44]/70 text-sm mb-4">{selected.position} &bull; {selected.school}</div>
              <div className="bg-[#f5f6f8] border-l-4 border-[#c8102e] p-4 mb-4">
                <Quote className="w-5 h-5 text-[#c8102e]/30 mb-2" />
                <p className="text-[#0a1f44]/85 italic text-sm leading-relaxed">&ldquo;{selected.quote}&rdquo;</p>
              </div>
              <p className="text-[#0a1f44]/75 text-sm leading-relaxed">{selected.bio}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
